import {
  PUBLIC_GALLERY_VARIANTS_BATCH_LIMIT,
  type PublicGalleryVariantBatchResponse,
  type PublicGalleryVariantRequest,
} from '@/api/services/report.service';

export const MAX_GALLERY_VARIANTS_PER_BATCH =
  PUBLIC_GALLERY_VARIANTS_BATCH_LIMIT;
export const MAX_GALLERY_VARIANT_RETRIES = 20;

export type GalleryVariantSize = 'THUMBNAIL' | 'LARGE';

export interface GalleryVariantRequestInput {
  attachmentId: string;
  size: GalleryVariantSize;
  legacySrc?: string;
}

export type GalleryVariantState =
  | {
      kind: 'loading';
      src?: undefined;
      usesDirectUrl: false;
    }
  | {
      kind: 'ready';
      src: string;
      usesDirectUrl: boolean;
    }
  | {
      kind: 'legacy-fallback';
      src: string;
      usesDirectUrl: boolean;
    }
  | {
      kind: 'placeholder';
      src?: undefined;
      usesDirectUrl: false;
    };

export interface PublicGalleryVariantResolverOptions {
  fetchBatch: (
    items: PublicGalleryVariantRequest[],
  ) => Promise<PublicGalleryVariantBatchResponse>;
  scopeKey?: string;
}

interface GalleryVariantEntry {
  input: GalleryVariantRequestInput;
  processingRetries: number;
  directRefreshes: number;
  state: GalleryVariantState;
  timer?: ReturnType<typeof setTimeout>;
}

const DEFAULT_PROCESSING_RETRY_MS = 1500;

const loadingState: GalleryVariantState = {
  kind: 'loading',
  usesDirectUrl: false,
};

const placeholderState: GalleryVariantState = {
  kind: 'placeholder',
  usesDirectUrl: false,
};

const createKey = (attachmentId: string, size: GalleryVariantSize): string =>
  `${attachmentId}:${size}`;

const isDirectUrl = (url: string): boolean =>
  url.startsWith('https://') || url.startsWith('http://');

const isUsableUrl = (url: string | undefined): url is string =>
  Boolean(url) && (isDirectUrl(url) || url.startsWith('/api/'));

const getRetryDelay = (retryAfterMs: number | undefined): number =>
  typeof retryAfterMs === 'number' && retryAfterMs > 0
    ? retryAfterMs
    : DEFAULT_PROCESSING_RETRY_MS;

/**
 * Cache and scheduler scoped to one public report token. It resolves variant
 * metadata only; binary image loading stays in AuthorizedImage.
 */
export class PublicGalleryVariantResolver {
  private readonly entries = new Map<string, GalleryVariantEntry>();
  private readonly listeners = new Set<() => void>();
  private readonly pendingKeys = new Set<string>();
  private flushScheduled = false;
  private isDisposed = false;

  constructor(private readonly options: PublicGalleryVariantResolverOptions) {}

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  getState(
    attachmentId: string,
    size: GalleryVariantSize,
  ): GalleryVariantState {
    return (
      this.entries.get(this.getEntryKey(attachmentId, size))?.state ??
      loadingState
    );
  }

  request(input: GalleryVariantRequestInput): void {
    if (this.isDisposed || !input.attachmentId) {
      return;
    }

    const key = this.getEntryKey(input.attachmentId, input.size);
    const existingEntry = this.entries.get(key);

    if (existingEntry) {
      if (!existingEntry.input.legacySrc && input.legacySrc) {
        existingEntry.input.legacySrc = input.legacySrc;
      }
      return;
    }

    this.entries.set(key, {
      input: { ...input },
      processingRetries: 0,
      directRefreshes: 0,
      state: loadingState,
    });
    this.enqueue(key);
    this.notify();
  }

  refreshDirect(attachmentId: string, size: GalleryVariantSize): boolean {
    if (this.isDisposed) {
      return false;
    }

    const key = this.getEntryKey(attachmentId, size);
    const entry = this.entries.get(key);

    if (
      !entry ||
      entry.state.kind !== 'ready' ||
      !entry.state.usesDirectUrl
    ) {
      return false;
    }

    if (entry.directRefreshes >= 1) {
      entry.state = placeholderState;
      this.notify();
      return false;
    }

    entry.directRefreshes += 1;
    entry.state = loadingState;
    this.enqueue(key);
    this.notify();
    return true;
  }

  dispose(): void {
    this.isDisposed = true;
    this.pendingKeys.clear();
    this.entries.forEach((entry) => {
      if (entry.timer) {
        clearTimeout(entry.timer);
      }
    });
    this.listeners.clear();
  }

  private enqueue(key: string): void {
    this.pendingKeys.add(key);

    if (this.flushScheduled) {
      return;
    }

    this.flushScheduled = true;
    queueMicrotask(() => {
      void this.flush();
    });
  }

  private async flush(): Promise<void> {
    this.flushScheduled = false;

    if (this.isDisposed || this.pendingKeys.size === 0) {
      return;
    }

    const keys = Array.from(this.pendingKeys);
    this.pendingKeys.clear();
    const batches = this.chunkKeys(keys);

    for (const batchKeys of batches) {
      const entries = batchKeys
        .map((key) => ({ key, entry: this.entries.get(key) }))
        .filter(
          (
            value,
          ): value is { key: string; entry: GalleryVariantEntry } =>
            Boolean(value.entry),
        );

      if (entries.length === 0 || this.isDisposed) {
        continue;
      }

      const items = entries.map(({ entry }) => ({
        attachmentId: entry.input.attachmentId,
        size: entry.input.size,
      }));

      try {
        const response = await this.options.fetchBatch(items);
        if (this.isDisposed) {
          return;
        }
        this.applyBatchResponse(entries, response);
      } catch {
        if (this.isDisposed) {
          return;
        }
        entries.forEach(({ entry }) => this.applyBatchFailure(entry));
      }
    }

    if (!this.isDisposed) {
      this.notify();
    }
  }

  private chunkKeys(keys: string[]): string[][] {
    const chunks: string[][] = [];
    for (
      let index = 0;
      index < keys.length;
      index += MAX_GALLERY_VARIANTS_PER_BATCH
    ) {
      chunks.push(keys.slice(index, index + MAX_GALLERY_VARIANTS_PER_BATCH));
    }
    return chunks;
  }

  private applyBatchResponse(
    entries: Array<{ key: string; entry: GalleryVariantEntry }>,
    response: PublicGalleryVariantBatchResponse,
  ): void {
    const results = new Map(
      response.results.map((result) => [
        createKey(result.attachmentId, result.size as GalleryVariantSize),
        result,
      ]),
    );

    entries.forEach(({ key, entry }) => {
      const result = results.get(
        createKey(entry.input.attachmentId, entry.input.size),
      );

      if (!result) {
        entry.state = placeholderState;
        return;
      }

      if (result.status === 'READY' && isUsableUrl(result.url)) {
        entry.state = {
          kind: 'ready',
          src: result.url,
          usesDirectUrl: isDirectUrl(result.url),
        };
        return;
      }

      if (result.status === 'PROCESSING') {
        this.scheduleProcessingRetry(key, entry, result.retryAfterMs);
        return;
      }

      entry.state = placeholderState;
    });
  }

  private scheduleProcessingRetry(
    key: string,
    entry: GalleryVariantEntry,
    retryAfterMs: number | undefined,
  ): void {
    // `MAX_GALLERY_VARIANT_RETRIES` is the maximum total number of metadata
    // requests, including the request that first returned PROCESSING.
    if (entry.processingRetries >= MAX_GALLERY_VARIANT_RETRIES - 1) {
      entry.state = placeholderState;
      return;
    }

    entry.processingRetries += 1;
    entry.state = loadingState;
    entry.timer = setTimeout(() => {
      entry.timer = undefined;
      if (this.isDisposed) {
        return;
      }
      this.enqueue(key);
    }, getRetryDelay(retryAfterMs));
  }

  private applyBatchFailure(entry: GalleryVariantEntry): void {
    if (entry.input.legacySrc) {
      entry.state = {
        kind: 'legacy-fallback',
        src: entry.input.legacySrc,
        usesDirectUrl: isDirectUrl(entry.input.legacySrc),
      };
      return;
    }

    entry.state = placeholderState;
  }

  private notify(): void {
    this.listeners.forEach((listener) => listener());
  }

  private getEntryKey(attachmentId: string, size: GalleryVariantSize): string {
    return `${this.options.scopeKey ?? 'public-report'}:${createKey(
      attachmentId,
      size,
    )}`;
  }
}
